import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const TYPE_COLORS = { machine: "#2563EB", blueprint: "#10B981", workorder: "#D97706", part: "#64748B", ecr: "#F59E0B", eco: "#7C3AED", config_item: "#0EA5E9", supplier: "#DC2626", scar: "#991B1B", cap: "#EA580C", audit: "#4F46E5" };
const TYPE_LABELS = { machine: "Machine", blueprint: "Blueprint", workorder: "Work Order", part: "Part", ecr: "ECR", eco: "ECO", config_item: "Config Item", supplier: "Supplier", scar: "SCAR", cap: "CAP", audit: "Audit" };

export default function KnowledgeGraphPage({ onLogout, userRole, isFeatureEnabled }) {
  const canvasRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [positions, setPositions] = useState({});
  const [filterType, setFilterType] = useState("");

  const fetchGraph = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/knowledge-graph`, { headers: getAuthHeaders() });
      setData(resp.data);
      // Initialize positions with force-directed-like layout
      const nodes = resp.data.nodes || [];
      const pos = {};
      const typeGroups = {};
      nodes.forEach(n => { if (!typeGroups[n.type]) typeGroups[n.type] = []; typeGroups[n.type].push(n.id); });
      const types = Object.keys(typeGroups);
      types.forEach((type, ti) => {
        const angle = (ti / types.length) * Math.PI * 2;
        const cx = 480 + Math.cos(angle) * 250;
        const cy = 340 + Math.sin(angle) * 200;
        typeGroups[type].forEach((id, ni) => {
          const subAngle = (ni / typeGroups[type].length) * Math.PI * 2;
          const r = 40 + typeGroups[type].length * 8;
          pos[id] = { x: cx + Math.cos(subAngle) * r, y: cy + Math.sin(subAngle) * r };
        });
      });
      setPositions(pos);
    } catch { toast.error("Failed to load graph"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchGraph(); }, [fetchGraph]);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const nodes = (data.nodes || []).filter(n => !filterType || n.type === filterType);
    const nodeIds = new Set(nodes.map(n => n.id));
    const edges = (data.edges || []).filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));

    // Draw edges
    ctx.strokeStyle = "#E2E8F0";
    ctx.lineWidth = 1;
    edges.forEach(e => {
      const s = positions[e.source]; const t = positions[e.target];
      if (s && t) { ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(t.x, t.y); ctx.stroke(); }
    });

    // Draw nodes
    nodes.forEach(n => {
      const p = positions[n.id];
      if (!p) return;
      const color = TYPE_COLORS[n.type] || "#64748B";
      const isSelected = selectedNode?.id === n.id;
      ctx.beginPath();
      ctx.arc(p.x, p.y, isSelected ? 10 : 7, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      if (isSelected) { ctx.strokeStyle = "#0F172A"; ctx.lineWidth = 2; ctx.stroke(); }
      ctx.fillStyle = "#0F172A";
      ctx.font = "9px monospace";
      ctx.textAlign = "center";
      ctx.fillText(n.label.substring(0, 15), p.x, p.y + 18);
    });
  }, [data, positions, filterType, selectedNode]);

  const handleCanvasClick = (e) => {
    if (!data) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    const nodes = data.nodes.filter(n => !filterType || n.type === filterType);
    for (const n of nodes) {
      const p = positions[n.id];
      if (p && Math.sqrt((x - p.x) ** 2 + (y - p.y) ** 2) < 12) { setSelectedNode(n); return; }
    }
    setSelectedNode(null);
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-4" data-testid="knowledge-graph-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Knowledge Graph" subtitle={`${data?.node_count || 0} entities · ${data?.edge_count || 0} relationships`} testId="kg-title" />
          <Button onClick={fetchGraph} variant="outline" className="rounded-none text-xs font-bold" data-testid="kg-refresh"><RefreshCw size={14} className="mr-1" />Refresh</Button>
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="kg-filters">
          <button onClick={() => setFilterType("")} className={`px-3 py-1.5 text-[10px] font-bold border ${!filterType ? "bg-[#0F172A] text-white border-[#0F172A]" : "text-[#64748B] border-[#E2E8F0]"}`}>All</button>
          {(data?.types || []).map(t => (
            <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-1.5 text-[10px] font-bold border ${filterType === t ? "text-white" : "border-[#E2E8F0]"}`} style={filterType === t ? { backgroundColor: TYPE_COLORS[t], borderColor: TYPE_COLORS[t] } : { color: TYPE_COLORS[t] }} data-testid={`kg-filter-${t}`}>{TYPE_LABELS[t] || t}</button>
          ))}
        </div>
        <div className="flex gap-4">
          <div className="flex-1 bg-white border border-[#E2E8F0] p-2">
            <canvas ref={canvasRef} width={960} height={680} className="w-full cursor-pointer" onClick={handleCanvasClick} data-testid="kg-canvas" />
          </div>
          {selectedNode && (
            <div className="w-64 bg-white border border-[#E2E8F0] p-4 shrink-0" data-testid="kg-detail">
              <div className="w-4 h-4 mb-2" style={{ backgroundColor: TYPE_COLORS[selectedNode.type] }} />
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{TYPE_LABELS[selectedNode.type] || selectedNode.type}</p>
              <p className="text-sm font-bold text-[#0F172A] mt-1">{selectedNode.label}</p>
              {selectedNode.meta && Object.entries(selectedNode.meta).filter(([,v]) => v).map(([k, v]) => (
                <p key={k} className="text-[11px] text-[#64748B] mt-0.5"><span className="text-[#94A3B8]">{k}:</span> {v}</p>
              ))}
              <p className="text-[10px] text-[#94A3B8] mt-2">Connections: {(data?.edges || []).filter(e => e.source === selectedNode.id || e.target === selectedNode.id).length}</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
